import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  useDiaryStyleStatus,
  useGenerateDiary,
} from "~/services/business/diary";
import { checkActionCost } from "~/services/business/credit";

/** 알림장에 첨부된 미디어 1건 */
export interface DraftMediaItem {
  seq: number;
  url: string;
  type: "image" | "video";
  /** 영상 썸네일(서버 생성 poster). 영상은 url(mp4)을 <img> 로 못 그리므로 이 값을 쓴다. */
  thumbUrl?: string | null;
}

interface Props {
  /** 선택된 수신 펫 (없으면 클릭 시 펫 선택 안내) */
  petSeq: number | null;
  /** 알림장에 붙은 전체 미디어(사진+영상) — 하이브리드 그리드에서 선택 */
  media: DraftMediaItem[];
  /** 현재 본문(메모 우선 근거로 전달) */
  memo?: string;
  /** 오늘의 활동 체크 요약 (AI 분석 근거) */
  activityNote?: string;
  /** 초안 생성 성공 시 본문/하이라이트 콜백 */
  onDrafted: (body: string, highlight?: string) => void;
  /** 좌측 라벨(예: "내용") — 있으면 라벨과 버튼을 한 줄 양끝에 배치 */
  title?: string;
}

/** AI 입력 이미지 캡(BE 샘플링과 동일 기준의 UX 상한). */
const MAX_IMAGES = 8;

/**
 * AI 알림장 — 상태 카드 + 하이브리드 미디어 선택 + "AI 초안 생성" 버튼.
 * 첨부 미디어를 그리드로 보여주고 캡까지 자동 체크, 선생님이 토글로 조정.
 * 미등록이어도 기본 프리셋으로 동작하며 등록 유도만 노출.
 */
export function NotifyAiDraft({
  petSeq,
  media,
  memo,
  activityNote,
  onDrafted,
  title,
}: Props) {
  const { history } = useRouter();
  const { data: status } = useDiaryStyleStatus();
  const generate = useGenerateDiary();

  const registered = !!status?.registered;

  // 자동 프리체크 — 이미지는 앞에서 최대 8장, 영상은 전부(BE가 키프레임 6장으로 캡)
  const autoChecked = useMemo(() => {
    const set = new Set<number>();
    let imgCount = 0;
    for (const m of media) {
      if (m.type === "image") {
        if (imgCount < MAX_IMAGES) {
          set.add(m.seq);
          imgCount += 1;
        }
      } else {
        set.add(m.seq);
      }
    }
    return set;
  }, [media]);

  // 첨부한 미디어를 자동 사용(이미지 최대 8장·영상 전부). 미리보기는 아래 "사진등록" 영역 하나만.
  const checkedImages = media.filter(
    (m) => m.type === "image" && autoChecked.has(m.seq),
  );
  const checkedVideos = media.filter(
    (m) => m.type === "video" && autoChecked.has(m.seq),
  );

  const wantMemo = !!memo?.trim() || !!activityNote?.trim();
  const wantMedia = checkedImages.length > 0 || checkedVideos.length > 0;
  // 입력 필수 — 메모(본문)·사진·영상 중 하나라도 있어야 초안 생성(빈 초안 과금 방지)
  const hasInput = wantMemo || wantMedia;
  // 버튼은 항상 활성(생성 중 제외) — 펫 미선택/입력 없음이면 클릭 시 안내만 띄운다.
  const canRun = !generate.isPending;

  // 아무 입력 없이 눌렀을 때 노출되는 안내
  const [showGuide, setShowGuide] = useState(false);

  // 크레딧 고지 — 서비스금액(DB) 단가 조회. 미디어=IMAGE, 텍스트=TEXT, 둘 다면 합산.
  const textCostQ = useQuery({
    queryKey: ["ai-diary-cost", "USE_AI_DIARY_TEXT"],
    queryFn: () => checkActionCost("USE_AI_DIARY_TEXT"),
    enabled: !!petSeq,
    staleTime: 5 * 60_000,
  });
  const imageCostQ = useQuery({
    queryKey: ["ai-diary-cost", "USE_AI_DIARY_IMAGE"],
    queryFn: () => checkActionCost("USE_AI_DIARY_IMAGE"),
    enabled: !!petSeq,
    staleTime: 5 * 60_000,
  });
  const textPrice = textCostQ.data?.totalCost ?? 0;
  const imagePrice = imageCostQ.data?.totalCost ?? 0;
  const expectedCost =
    (wantMedia ? imagePrice : 0) + (wantMemo ? textPrice : 0);

  // 생성 진척률 — 단일 POST라 실측 불가, 2단계 파이프라인에 맞춘 단계+진행바 시뮬레이션.
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("");
  useEffect(() => {
    if (!generate.isPending) return;
    const stages = wantMedia
      ? [
          "사진·영상 분석 중",
          "특이사항 정리 중",
          "내 스타일로 작성 중",
          "마무리 중",
        ]
      : ["내용 정리 중", "내 스타일로 작성 중", "마무리 중"];
    const est = wantMedia ? 16000 : 7000; // 예상 소요(ms)
    const tick = 300;
    let p = 4;
    let si = -1;
    setProgress(p);
    const id = setInterval(() => {
      p = Math.min(92, p + (92 * tick) / est);
      setProgress(Math.round(p));
      const idx = Math.min(
        stages.length - 1,
        Math.floor((p / 92) * stages.length),
      );
      if (idx !== si) {
        si = idx;
        setStage(stages[idx]);
      }
    }, tick);
    return () => clearInterval(id);
  }, [generate.isPending, wantMedia]);
  useEffect(() => {
    if (generate.isSuccess) setProgress(100);
  }, [generate.isSuccess]);

  const run = () => {
    if (!petSeq || !hasInput) {
      // 펫 미선택이거나 입력이 없으면 생성하지 않고 안내만 노출
      setShowGuide(true);
      return;
    }
    setShowGuide(false);
    generate.mutate(
      {
        petSeq,
        imageFileSeqs: checkedImages.map((m) => m.seq),
        videoFileSeqs: checkedVideos.map((m) => m.seq),
        memo: memo?.trim() ? memo.trim() : undefined,
        activityNote: activityNote?.trim() ? activityNote.trim() : undefined,
      },
      { onSuccess: (r) => onDrafted(r.body, r.highlight) },
    );
  };

  return (
    <div className="mb-2 flex flex-col gap-1.5">
      {/* 한 줄: (좌) 라벨 · (우) 작은 버튼 + 스타일 등록/학습 상태 */}
      <div className="flex items-center justify-between gap-2">
        {title ? (
          <span className="text-sm font-medium">{title}</span>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {!registered && (
            <button
              type="button"
              className="text-[11px] text-[#B89A6B] underline"
              onClick={() => history.push("/business/layout/notify-style")}
            >
              내 문체 등록
            </button>
          )}
          {registered && !status?.styleReady && (
            <span className="text-[11px] text-[#BA7517]">학습 처리 중</span>
          )}
          <button
            type="button"
            disabled={!canRun}
            className="inline-flex items-center gap-1 rounded-full bg-[#FF9934] px-3 py-1 text-[12px] font-medium text-white disabled:opacity-40"
            onClick={run}
          >
            {generate.isPending
              ? `초안 생성 중… ${progress}%`
              : "✨ AI알림장 초안생성" +
                (hasInput && expectedCost > 0 ? ` · ${expectedCost}C` : "")}
          </button>
        </div>
      </div>

      {/* 생성 진척률 — 단계 설명 + 진행바 */}
      {generate.isPending && (
        <div className="rounded-lg border border-[#FFE0B3] bg-[#FFF7E6] px-3 py-2">
          <div className="mb-1 flex items-center justify-between text-[11px] text-[#8A6A2F]">
            <span>✨ {stage || "준비 중"}…</span>
            <span className="font-medium">{progress}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F2E4CC]">
            <div
              className="h-full rounded-full bg-[#FF9934] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* 크레딧 고지 — 생성 성공 시 차감 */}
      {petSeq && hasInput && !generate.isPending && !generate.isSuccess && (
        <div className="text-[11px] text-[#888780]">
          생성에 성공하면 <b className="text-[#BA7517]">{expectedCost}크레딧</b>
          이 차감돼요
          {wantMedia && wantMemo
            ? " (사진 " + imagePrice + " + 텍스트 " + textPrice + ")"
            : ""}
          .
        </div>
      )}
      {generate.isSuccess && generate.data && (
        <div className="text-[11px] text-[#1D9E75]">
          ✓ 초안 완성 · {generate.data.creditCharged}크레딧 차감
          {generate.data.stubbedYn === "Y" ? " (무료·스텁)" : ""}
        </div>
      )}

      {showGuide && !hasInput && !generate.isPending && (
        <div className="rounded-lg border border-[#FFE0B3] bg-[#FFF7E6] px-3 py-2 text-[12px] leading-relaxed text-[#8A6A2F]">
          {!petSeq ? (
            <>
              먼저 위에서 <b>받는 펫</b>을 선택해주세요.
            </>
          ) : (
            <>
              오늘 있었던 간단한 특이사항을 적거나 사진·영상을 첨부한 뒤{" "}
              <b>AI 초안</b>을 누르면, 등록한 스타일대로 알림장 초안을
              만들어드려요.
            </>
          )}
        </div>
      )}
      {generate.isError && (
        <div className="text-[11px] text-[#D4537E]">
          초안 생성에 실패했어요. 잠시 후 다시 시도해주세요.
        </div>
      )}
    </div>
  );
}
